/*@flow*/
import type { StructType } from './types.js'; import isArray from './isArray';
import isFunction from './isFunction';
import isObject from './isObject';
import type {
  ValidationErrorT,
  ValidationResultT,
  ValidationTypeT,
} from './types';
import struct from './struct';
import Any from './Any';
import list from './list';
import union from './union';
import isNil from './isNil';
import stringify from './stringify';
import getTypeName from './getTypeName';
import isType from './isType';
import tNumber from './Number';
import tString from './String';

type OptionsT = { context?: any, path?: string[] } | string[] | null;

export const ValidationError: StructType = struct(
  {
    message: Any,
    actual: Any,
    expected: Any,
    path: list(union([tString, tNumber])),
  },
  'ValidationError'
);

const hasOwn = Object.prototype.hasOwnProperty;

function getDefaultValidationErrorMessage(actual, expected, path) {
  let expectedName = getTypeName(expected);
  let to = path.length
    ? '/' + path.join('/') + ': ' + expectedName
    : expectedName;
  return 'Invalid value ' + stringify(actual) + ' supplied to ' + to;
}

function getValidationErrorMessage(actual, expected, path, context) {
  if (isFunction(expected.getValidationErrorMessage)) {
    return expected.getValidationErrorMessage(actual, path, context);
  }
  return getDefaultValidationErrorMessage(actual, expected, path);
}

function newError(actual, expected, path, context): ValidationErrorT {
  const message = getValidationErrorMessage(actual, expected, path, context);
  return (ValidationError({
    message,
    actual,
    expected,
    path,
  }): any);
}

export const ValidationResult: (any) => ValidationResultT = struct(
  {
    errors: list(ValidationError),
    value: Any,
  },
  'ValidationResult'
);

ValidationResult.prototype.isValid = function() {
  return !this.errors.length;
};

ValidationResult.prototype.firstError = function() {
  return this.isValid() ? null : this.errors[0];
};

ValidationResult.prototype.toString = function() {
  if (this.isValid()) {
    return '[ValidationResult, true, ' + stringify(this.value) + ']';
  }
  return (
    '[ValidationResult, false, (' +
    this.errors
      .map(function(err) {
        return stringify(err.message);
      })
      .join(', ') +
    ')]'
  );
};

function recurse(
  value: any,
  type: ValidationTypeT,
  path: string[],
  options?: OptionsT
) {
  if (isType(type)) {
    return validators[type.meta.kind](value, type, path, options);
  }
  return validators.es6classes(value, type, path, options);
}

//
//
//
//
//
// Validators
//
//
//
//
const validators = {};

validators.es6classes = (value, type, path, options): ValidationResultT => {
  let errors = [];
  let options2 = options || {};
  let context = isArray(options2) ? {} : options2.context;

  if (!(value instanceof type)) {
    const err = newError(value, type, path, context);
    errors = [err];
  }
  return (ValidationResult({ value, errors }): any);
};

// irreducibles and enums
validators.irreducible = validators.enums = (x, type, path, options) => ({
  value: x,
  errors: type.is(x) ? [] : [newError(x, type, path, options.context)],
});

validators.list = (x, type, path, options) => {
  // x should be an array
  if (!isArray(x)) {
    return {
      value: x,
      errors: [newError(x, type, path, options.context)],
    };
  }

  let ret = { value: [], errors: [] };

  // Every item should be of type `type.meta.type`
  x.forEach((item0, i) => {
    let item = recurse(item0, type.meta.type, path.concat(i), options);
    ret.value[i] = item.value;
    ret.errors = ret.errors.concat(item.errors);
  });

  return ret;
};

validators.subtype = (x, type, path, options): ValidationResultT => {
  // x should be a valid inner type
  let ret = recurse(x, type.meta.type, path, options);
  // if (ret.errors.length && type.meta.type.name === 'Irreducible') {
  //   return ret;
  // }

  // x should satisfy the predicate
  if (!type.meta.predicate(ret.value, path, options.context)) {
    ret.errors = [newError(x, type, path, options.context)];
  }

  return ret;
};

validators.maybe = (x, type, path, options) =>
  isNil(x)
    ? { value: x, errors: [] }
    : recurse(x, type.meta.type, path, options);

validators.struct = (x, type, path, options) => {
  // x should be an object
  if (!isObject(x)) {
    return {
      value: x,
      errors: [newError(x, type, path, options.context)],
    };
  }

  // [optimization]
  if (type.is(x)) return { value: x, errors: [] };

  let ret = { value: {}, errors: [] };
  let props = type.meta.props;

  // Every item should be of type `props[name]`
  Object.keys(props).forEach((name) => {
    if (hasOwn.call(props, name)) {
      let actual = x[name];
      let prop = recurse(actual, props[name], path.concat(name), options);
      ret.value[name] = prop.value;
      ret.errors = ret.errors.concat(prop.errors);
    }
  });

  // Check for super validation rules in the "meta" property.
  const { rules } = type.meta;
  // type ErrorMessage = string;
  // type ErrorConfig = {message: string, path: string};
  // type Rule = Value => undefined | ErrorMessage | ErrorConfig;
  // type Rules = Rule[];
  if (isArray(rules)) {
    const messages = rules.map((rule) => rule(x)).filter(Boolean);
    if (messages.length > 0) {
      ret.errors = ret.errors.concat(
        messages.map((v) => {
          let path2 = [];
          let message = v;
          if (typeof v === 'object') {
            path2 = path2.concat(v.path);
            message = v.message;
          }
          const payload = {
            message,
            actual: null,
            expected: null,
            path: path.concat(path2),
          };
          return ValidationError(payload);
        })
      );
    }
  }

  if (!ret.errors.length) {
    ret.value = new type(ret.value); // eslint-disable-line new-cap
  }
  return ret;
};

validators.tuple = (x, type, path, options) => {
  let types = type.meta.types;
  let len = types.length;

  // x should be an array of at most `len` items
  if (!isArray(x) || x.length > len) {
    return {
      value: x,
      errors: [newError(x, type, path, options.context)],
    };
  }

  let ret = { value: [], errors: [] };
  // every item should be of type `types[i]`
  for (let i = 0; i < len; i++) {
    let item = recurse(x[i], types[i], path.concat(i), options);
    ret.value[i] = item.value;
    ret.errors = ret.errors.concat(item.errors);
  }
  return ret;
};

validators.dict = (x, type, path, options) => {
  // x should be an object
  if (!isObject(x)) {
    return {
      value: x,
      errors: [newError(x, type, path, options.context)],
    };
  }

  let ret = { value: {}, errors: [] };
  // every key should be of type `domain`
  // every value should be of type `codomain`
  Object.keys(x).forEach((k) => {
    if (hasOwn.call(x, k)) {
      let subpath = path.concat(k);
      let key = recurse(k, type.meta.domain, subpath, options);
      let item = recurse(x[k], type.meta.codomain, subpath, options);
      ret.value[k] = item.value;
      ret.errors = ret.errors.concat(key.errors, item.errors);
    }
  });
  return ret;
};

validators.union = (x, type, path, options) => {
  let ctor = type.dispatch(x);
  return isFunction(ctor)
    ? recurse(x, ctor, path.concat(type.meta.types.indexOf(ctor)), options)
    : {
        value: x,
        errors: [newError(x, type, path, options.context)],
      };
};

validators.intersection = (x, type, path, options) => {
  let types = type.meta.types;
  let len = types.length;

  let ret = { value: x, errors: [] };
  let nrOfStructs = 0;
  // x should be of type `types[i]` for all i
  for (let i = 0; i < len; i++) {
    if (types[i].meta.kind === 'struct') {
      nrOfStructs++;
    }
    let item = recurse(x, types[i], path, options);
    ret.errors = ret.errors.concat(item.errors);
  }
  if (nrOfStructs > 1) {
    ret.errors.push(newError(x, type, path, options.context));
  }
  return ret;
};

validators.interface = (x, type, path, options) => {
  // x should be an object
  if (!isObject(x)) {
    return {
      value: x,
      errors: [newError(x, type, path, options.context)],
    };
  }

  let ret = { value: {}, errors: [] };
  let props = type.meta.props;

  // Every item should be of type `props[name]`
  Object.keys(props).forEach((name) => {
    let prop = recurse(x[name], props[name], path.concat(name), options);
    ret.value[name] = prop.value;
    ret.errors = ret.errors.concat(prop.errors);
  });

  return ret;
};

function validate(
  value: any,
  type: ValidationTypeT,
  options?: OptionsT
): ValidationResultT {
  let options2 = options || {};
  let path = isArray(options2) ? options2 : options2.path;
  path = path || [];
  const value2 = recurse(value, type, path, options2);
  return (ValidationResult(value2): any);
}

validate.validators = validators;

export default validate;
